import { ConsultationTimeField } from "@/components/consultation-time-field";
import { ReadOnlyField } from "@/components/read-only-field";
import { SpinnerLoader } from "@/components/spinner-loader";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { SymptomsField } from "@/features/hmo-consultation/components/symptoms-field";
import { AddressInput } from "@/features/hmo-consultation/components/address-input";
import { BackButton } from "@/features/hmo-consultation/components/back-button";
import { ContactMedium } from "@/features/hmo-consultation/components/contact-medium";
import { DiscomfortLevel } from "@/features/hmo-consultation/components/discomfort-level";
import { DOB } from "@/features/hmo-consultation/components/dob-field";
import { EmailField } from "@/features/hmo-consultation/components/email-field";
import { GenderField } from "@/features/hmo-consultation/components/gender-field";
import { PharmacyAndDeliverySelectionField } from "@/features/hmo-consultation/components/pharmacy-and-delivery-selection-field";
import { PhoneNumber } from "@/features/hmo-consultation/components/phone-number-field";
import { PrevConsultationCard } from "@/features/hmo-consultation/components/prev-consultation-card";
import { useHMOStore } from "@/features/hmo-consultation/hooks/use-hmo-store";
import { FormSchemaType } from "@/features/hmo-consultation/schema";
import {
  formatValues,
  extraValidate,
  hideInputForFollowUp,
  updateUserInfo,
  getPrevStepInCreateConsultationForm,
} from "@/features/hmo-consultation/utils";
import {
  getLeadwayPharmaciesQuery,
  getWellaHealthPharmaciesQuery,
} from "@/graphql/queries";
import { toast } from "@/hooks/use-toast";
import {
  clearCampaignId,
  getCampaignId,
  trackMixpanelEvent,
} from "@/lib/mixpanel";
import { restApi } from "@/lib/rest-api-client";
import { formatName, isDate, leadwayIds } from "@/lib/utils";
import { useLazyQuery as useLazyApolloQuery } from "@apollo/client";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { FieldErrors, useFormContext } from "react-hook-form";

export function NewConsultation() {
  const providerFeatures = useHMOStore((s) => s.providerFeatures);
  const hasPharmacyFeature = !!providerFeatures?.hasPharmacyFeature;
  const hasAddressFeature = !!providerFeatures?.hasAddressFeature;

  const {
    formState: { errors },
    handleSubmit,
    setError,
    watch,
    setValue,
  } = useFormContext<FormSchemaType>();
  const [submissionError, setSubmissionError] = useState<null | string>(null);
  const [loading, setLoading] = useState(false);

  const appState = useHMOStore();
  const providerId = appState?.partnerInfo?.providerId;

  const isLeadwayProvider = !!providerId && leadwayIds.includes(providerId);

  const [getLeadwayPharmacies, { data: leadwayData, loading: loadingLeadway }] =
    useLazyApolloQuery(getLeadwayPharmaciesQuery);
  const [
    getWellaHealthPharmacies,
    { data: wellaHealthData, loading: loadingWellaHealth },
  ] = useLazyApolloQuery(getWellaHealthPharmaciesQuery);

  const pharmacyArray = isLeadwayProvider
    ? leadwayData?.getPharmaciesFromLeadway?.data
    : wellaHealthData?.getPharmaciesFromWellaHealth?.data;

  const loadingPharmacies = isLeadwayProvider
    ? loadingLeadway
    : loadingWellaHealth;

  // const street = watch("street") || "";
  const state = watch("state") || "";
  const lga = watch("lga") || "";

  const showPharmacySelectInput = useMemo(() => {
    return !!state && !!lga && !!pharmacyArray && pharmacyArray?.length > 0;
  }, [state, lga, pharmacyArray]);

  useEffect(() => {
    if (state && lga && hasPharmacyFeature) {
      if (isLeadwayProvider) {
        getLeadwayPharmacies({ variables: { state, lga } });
      } else {
        getWellaHealthPharmacies({ variables: { state, lga } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, lga, isLeadwayProvider]);

  const isFollowUp = watch("isFollowUp");
  const selectedPrevConsultation = useHMOStore(
    (s) => s.selectedPrevConsultation,
  );
  const isHospitalConsultation = useHMOStore((s) => s.isHospitalConsultation);
  const selectedHospital = useHMOStore((s) => s.selectedHospital);
  const prevStep = getPrevStepInCreateConsultationForm(
    !!isFollowUp,
    !!isHospitalConsultation,
  );

  const selectedDoctor = useHMOStore((s) => s.selectedDoctor);
  const doctorName = `Dr. ${selectedDoctor?.firstName} ${selectedDoctor?.lastName}`;
  const selectedDate = useHMOStore((s) => s.selectedDate);

  const hmoUserObj = useHMOStore((s) => s.userData);
  const subdomain = useHMOStore((s) => s.partnerInfo?.subdomain);

  const isEmailEditable = !hmoUserObj?.email || Boolean(errors["email"]);
  // const isGenderEditable =
  //   !hmoUserObj?.gender ||
  //   Boolean(errors["gender"]) ||
  //   !/^(Male|Female|male|female)$/.test(hmoUserObj?.gender);
  const isDOBEditable =
    !hmoUserObj?.dob || Boolean(errors["dob"]) || !isDate(hmoUserObj?.dob);
  const enrolleeName = formatName(hmoUserObj?.firstName, hmoUserObj?.lastName);

  const showAddressInput = hasPharmacyFeature || hasAddressFeature;

  async function onSubmit(values: FormSchemaType) {
    try {
      setSubmissionError("");
      setLoading(true);
      const { isValid, error } = extraValidate({
        hasPharmacyFeature: !!hasPharmacyFeature && !!showPharmacySelectInput,
        values,
        pharmacies: pharmacyArray!,
        isFollowUp: false,
      });

      if (!isValid) {
        if (error.name === "none") {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
          return;
        }

        setError(
          error.name,
          { type: "manual", message: error?.message },
          { shouldFocus: true },
        );
        setSubmissionError("Missing Fields!");
        setLoading(false);
        return;
      }

      updateUserInfo(values, hmoUserObj).catch((err) => console.error(err));

      // @ts-expect-error need to type form schema to include 'time' field
      if (values.time) {
        // @ts-expect-error need to type form schema to include 'time' field
        const dateString = `${selectedDate}T${values.time}`;
        // @ts-expect-error need to type form schema to include 'time' field
        values.time = dateString;
      }

      const formattedData = formatValues(
        values,
        (pharmacyArray || []).length > 0,
        !!isHospitalConsultation,
      );
      const response = await restApi.post(
        "/consultations/create",
        formattedData,
      );

      const consultationData = response.data.data;
      trackMixpanelEvent("consultation_created", {
        source: "weblink",
        consultationId: consultationData?._id ?? "",
        medium: formattedData.contactMedium,
        partner: subdomain
          ? subdomain.charAt(0).toUpperCase() + subdomain.slice(1)
          : "",
      });

      let consultationURL = consultationData.url;
      try {
        const campaignId = getCampaignId();
        if (campaignId) {
          const url = new URL(consultationURL);
          url.searchParams.set("campaign-id", campaignId);
          consultationURL = url.toString();
        }
        clearCampaignId();
      } catch (error) {
        console.warn(
          "[Mixpanel] Failed to append campaign-id to consultation URL",
          error,
        );
      }
      window.open(consultationURL, "_self");
    } catch (error) {
      setLoading(false);
      console.error(error);

      if (axios.isAxiosError(error)) {
        setSubmissionError(error.response?.data.message);
        return;
      }

      if (typeof error === "object" && error !== null && "message" in error) {
        setSubmissionError(error.message as string);
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BackButton step={prevStep} />
      <div className="space-y-5">
        {isFollowUp && selectedPrevConsultation && (
          <PrevConsultationCard consultationInfo={selectedPrevConsultation} />
        )}

        {isHospitalConsultation && !isFollowUp && (
          <ReadOnlyField
            name="hospital"
            label="Hospital"
            value={selectedHospital?.name || ""}
            required={true}
          />
        )}

        {isHospitalConsultation && !isFollowUp && (
          <ReadOnlyField
            name="doctor"
            label="Doctor"
            value={doctorName}
            required={true}
          />
        )}

        {isHospitalConsultation && !isFollowUp && (
          <div>
            <FormLabel required={true}>
              Select a time for your consultation
            </FormLabel>
            <ConsultationTimeField
              doctorProfileId={selectedDoctor?._id}
              selectedDate={new Date(selectedDate!)}
              onChange={(value) => {
                // @ts-expect-error need to type the form schema to include "time"
                setValue("time", value);
              }}
            />
          </div>
        )}

        <ReadOnlyField
          name="name"
          label="Name"
          value={enrolleeName || ""}
          required={true}
        />

        <GenderField
        // readOnly={!isGenderEditable}
        />

        <DOB readOnly={!isDOBEditable} />

        <PhoneNumber />

        <EmailField readOnly={!isEmailEditable} />

        {/* todo: fix field disappearing after resolving error */}
        {!hideInputForFollowUp(!!isFollowUp, Boolean(errors?.symptoms)) && (
          <SymptomsField />
        )}

        {!hideInputForFollowUp(
          !!isFollowUp,
          Boolean(errors?.discomfortLevel),
        ) && <DiscomfortLevel />}

        {/* {!hideInputForFollowUp(!!isFollowUp, Boolean(errors?.firstNotice)) && (
          <FirstNotice />
        )} */}

        <ContactMedium />

        {showAddressInput && <AddressInput />}

        {loadingPharmacies && <LoadingPharmacies />}

        {hasPharmacyFeature && showPharmacySelectInput && (
          <PharmacyAndDeliverySelectionField
            loadingPharmacies={loadingPharmacies}
            pharmacies={pharmacyArray}
          />
        )}

        {/* <AdditionalInformation /> */}

        <div className="btn-block">
          {errors && Object.keys(errors || {})?.length > 0 && (
            <>
              {/* <p className="text-red-500 text-sm">
                Missing fields! Please crosscheck your form or contact support.
              </p> */}
              <FieldErrorsSummary errors={errors} />
            </>
          )}
          {submissionError && (
            <p className="text-red-500 text-sm mt-2">{submissionError}</p>
          )}

          <div className="mt-6">
            <Button
              formNoValidate={true}
              type="submit"
              loading={loading}
              className="w-full"
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}

type FieldErrorsSummaryProps = {
  errors: FieldErrors<FormSchemaType>;
};

const FieldErrorsSummary = (props: FieldErrorsSummaryProps) => {
  const { errors } = props;

  if (!errors) return null;

  console.error("errors: ", { errors });

  const fieldErrorsTuple = (Object.keys(errors) as (keyof typeof errors)[])
    .map((item) => {
      // todo: I'm not entirely sure why I'm doing this. Why not go ahead
      // to let all the field errors show instead of restricting it? 🤔
      const fieldsToCheck = [
        "dob",
        "type",
        "time",
        "gender",
        "doctor",
        "lastName",
        "symptoms",
        "firstName",
        "providerId",
        "pharmacyCode",
        "pharmacyName",
        "companyId",
        "isDelivery",
        "patient",
      ];

      if (!fieldsToCheck.includes(item)) return null;

      return [item, errors[item]?.message];
    })
    .filter(Boolean);

  return (
    <div className="text-red-500 mt-2">
      {fieldErrorsTuple?.map((item) => {
        const [field, error] = item || [];

        return (
          <div key={field} className="text-red-500 text-sm">
            {error}
          </div>
        );
      })}
    </div>
  );
};

function LoadingPharmacies() {
  return (
    <div className="flex flex-col items-center gap-1">
      <SpinnerLoader className="w-5 h-5" />
      <span className="text-gray-500 text-xs">Loading pharmacies</span>
    </div>
  );
}
